import React from 'react';
import { Box, Typography, Grid, Chip, Divider } from '@mui/material';
import { Palette, Flower, Sparkle } from '@phosphor-icons/react';
import useThemeStyles from '../../../../theme/useThemeStyles';

const StyleSection = ({ style }) => {
  const styles = useThemeStyles();

  if (!style) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Palette size={24} style={{ marginRight: 12, color: '#64748b' }} />
          <Typography variant="h5" sx={{ fontWeight: 600, ...styles.text.primary }}>
            Style & Theme
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" sx={{ ...styles.text.secondary }}>
            No style information added yet
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Palette size={24} style={{ marginRight: 12, color: '#64748b' }} />
        <Typography variant="h5" sx={{ fontWeight: 600, ...styles.text.primary }}>
          Style & Theme
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Theme */}
        {style.theme && (
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Theme
              </Typography>
              <Chip label={style.theme} color="primary" />
            </Box>
          </Grid>
        )}

        {/* Tone */}
        {style.tone && (
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Tone
              </Typography>
              <Chip label={style.tone} variant="outlined" />
            </Box>
          </Grid>
        )}

        {/* Colors */}
        {(style.colors?.length > 0 || style.customColors?.length > 0 || style.colorPalette) && (
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Color Palette
              </Typography>
              {style.colorPalette && (
                <Typography variant="body2" sx={{ mb: 1, ...styles.text.secondary }}>
                  {style.colorPalette}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {style.colors?.map((color, index) => (
                  <Chip
                    key={index}
                    label={color}
                    sx={{
                      bgcolor: color,
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                ))}
                {style.customColors?.map((color, index) => (
                  <Chip
                    key={`custom-${index}`}
                    label={color}
                    sx={{
                      bgcolor: color,
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Grid>
        )}

        {/* Season */}
        {style.season && (
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Season
              </Typography>
              <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                {style.season}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Decoration Style */}
        {style.decorationStyle && (
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                Decoration Style
              </Typography>
              <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                {style.decorationStyle}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Flower Preferences */}
        {style.flowerPreferences?.length > 0 && (
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Flower size={20} style={{ marginRight: 8, color: '#64748b' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, ...styles.text.primary }}>
                  Flower Preferences
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {style.flowerPreferences.map((flower, index) => (
                  <Chip key={index} label={flower} variant="outlined" />
                ))}
              </Box>
            </Box>
          </Grid>
        )}

        {/* Notes */}
        {(style.notes || style.inspirationNotes || style.specialElements) && (
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            {style.notes && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                  Style Notes
                </Typography>
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  {style.notes}
                </Typography>
              </Box>
            )}
            {style.inspirationNotes && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Sparkle size={20} style={{ marginRight: 8, color: '#64748b' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, ...styles.text.primary }}>
                    Inspiration Notes
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  {style.inspirationNotes}
                </Typography>
              </Box>
            )}
            {style.specialElements && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, ...styles.text.primary }}>
                  Special Elements
                </Typography>
                <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                  {style.specialElements}
                </Typography>
              </Box>
            )}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default StyleSection;

